import i18n from 'i18next';
import { extractIBAN } from "ibantools";

import { Bank } from "types";

/**
 * Removes all spaces from an IBAN value for backend submission
 * @param value - Formatted IBAN value with spaces
 * @returns IBAN without spaces
 */
export function cleanIban(value: string): string {
  return value.replace(/\s/g, "");
}

/**
 * Formats an IBAN value according to the standard format
 * Format: XX00 0000 0000 0000 0000 0 (21 characters total)
 * @param value - Raw IBAN value to format
 * @returns Formatted IBAN with spaces
 */
export function formatIban(value: string): string {
  // Remove all spaces and convert to uppercase
  const cleaned = cleanIban(value).toUpperCase();

  // Limit to 21 characters
  const limited = cleaned.slice(0, 21);

  // Format: XX00 0000 0000 0000 0000 0
  // Add spaces after positions 4, 8, 12, 16, 20
  let formatted = "";
  for (let i = 0; i < limited.length; i++) {
    if (i === 4 || i === 8 || i === 12 || i === 16 || i === 20) {
      formatted += " ";
    }
    formatted += limited[i];
  }

  return formatted;
}

//TODO: CHECK!!
const SWISS_BANK_CODE_MAP: Record<string, Bank> = {
  // PostFinance (2 codes: old 00700, new 09000 after becoming full bank in 2013)
  '00700': 'POST_FINANCE',
  '09000': 'POST_FINANCE',
  '00836': 'MIGROSBANK',
  '00735': 'CEMBRA_MONEY_AG',
  '00685': 'CORNERCARD_KREDIT',
  '00774': 'BANK_NOW',
  '00632': 'SWISSCARD',
  '00648': 'VISECA_CARD',

  // Leasing companies with Swiss bank codes
  '00841': 'LEASING_FORCE_AG',
  '00847': 'MULTILEASE_AG',

  // Note: The following don't have Swiss bank clearing codes as they are:
  // - Credit platforms (CREDITGATE24, BOB_FINANCE, LEND, CASHGATE, ENY_FINANC)
  // - Loyalty programs (MIGROS_CUMULUS, COOP_SUPERCARD)
  // - Store cards (IKEA_FAMILY_CARTE, MEDIA_MARKT_SHOPPING_CARD, MANOR, CONFORAMA)
  // - Card programs (USB_KEY4_MASTERCARD, SWISS_MILES_AND_MORE, TSC_TRAVEL_MASTERCARD, AMERICAN_EXPRESS_CARD)
  // - Automotive leasing (MERCEDES_BENZ_FINANCIAL_SERVICE, AMAG, BMW_LEASING, PSA_FINANCE_SA, RCI_FINANCE_SA, FMCC_FINANCE)
  // These won't appear in employee salary IBANs
};

interface BankLookupResult {
  success: boolean;
  bankCode?: string;
  bank?: Bank;
  error?: string;
}

/**
 * Extract bank information from a Swiss IBAN
 * @param iban - Swiss IBAN (with or without spaces)
 * @returns Bank enum value (e.g., 'POST_FINANCE', 'MIGROSBANK')
 */
export function extractBankFromIban(iban: string): BankLookupResult {
  const cleanedIban = cleanIban(iban).toUpperCase();

  // Validate using ibantools
  const ibanInfo = extractIBAN(cleanedIban);

  if (!ibanInfo || !ibanInfo.valid) {
    return {
      success: false,
      error: i18n.t('bankCredit.iban.error'),
    };
  }

  // For Swiss IBANs, extract bank code manually
  // Swiss IBAN format: CHkk BBBB B000 0000 0000 0
  // Bank code is positions 4-8 (5 digits after country code and check digits)
  if (cleanedIban.substring(0, 2) !== 'CH') {
    return {
      success: false,
      error: i18n.t('bankCredit.iban.onlySwiss'),
    };
  }

  const bankCode = cleanedIban.substring(4, 9); // Extract 5-digit bank code
  if (!bankCode || bankCode.length !== 5) {
    return {
      success: false,
      error: i18n.t('bankCredit.iban.cannotExtractBank'),
    };
  }

  const bank: Bank | undefined = SWISS_BANK_CODE_MAP[bankCode];
  return { success: true, bank: bank ?? 'OTHER' };
}

/**
 * Validate Swiss IBAN format
 * @param iban - IBAN to validate
 * @returns true if valid Swiss IBAN
 */
export function isValidSwissIban(iban: string): boolean {
  const cleanedIban = cleanIban(iban).toUpperCase();
  const ibanInfo = extractIBAN(cleanedIban);

  return !!(ibanInfo && ibanInfo.valid && ibanInfo.countryCode === 'CH');
}
